import {CodeModelList} from "../../../models/codes/Code";
import {
  applySnapshot,
  flow,
  getEnv,
  getRoot,
  IAnyModelType,
  onSnapshot,
  getSnapshot,
  resolveIdentifier,
  types
} from "mobx-state-tree";
import {TableColumn} from "../../tables/ScanbuyTable";
import {TagSelectState} from "../../elements/tags/TagSelectState";
import {GroupSelectState} from "../../elements/groups/GroupSelectState";
import Group from "../../../models/Group";
import {ModalState} from "../../elements/modal/GenericModal";
import {GenericToastState} from "../../elements/toast/GenericToastState";
import DeleteState from "../delete/DeleteState";
import {CodeFilterSelectState} from "../../elements/filters/CodeFilterSelectState";
import lodash from "lodash";
import {PageTemplateSelectState} from "../../elements/pageTemplate/PageTemplateSelectState";
import {TagModel as Tag} from "../../../models/tags/TagModel";
import {CodeOwnerFilterSelectState} from "../../elements/filters/CodeOwnerFilterSelectState";
import QrTemplate from "../../../models/QrTemplates";
// import TemplateSelect from "src/components/elements/templates/TemplateSelect";
import {QrTemplateSelectState} from "../../elements/qrtemplates/QrTemplateSelectState";
import {codeBehavior, filterSessionConstants, formConstants} from "../../../utils/constants";
import {DimensionSelectViewState} from "../../elements/dimensions/dimensionSelectViewState";
import {TagFilterSelectState} from "../../elements/filters/TagFilterSelectState";
import {Modal2State} from "../../elements/modal2/GenericModal2";
import {GroupFilterSelectState} from "../../elements/filters/GroupFilterSelectState";
import {getBase64String, isEpsFormat} from "../../../utils/common";
import DataExportTypeStore from "../../../stores/domain/DataExportTypeStore";
import {
  applySearchParamsFromFilters, clearAllFilters,
  loadFiltersFromSession,
  persistFiltersToSession
} from "../../../utils/filterStorage";
import AuditViewState from "../audit/AuditViewState";

const CodeListState = types
  .model('CodeListState', {
    store: types.late(() => CodeModelList), // <-- hold the store
    columns: types.maybeNull(types.array(types.late(() => TableColumn))),
    tagSelectStore: types.maybeNull(types.reference(TagSelectState)),
    groupSelectStore: types.maybeNull(types.reference(GroupSelectState)),
    qrtemplateSelectStore:types.maybeNull(types.reference(QrTemplateSelectState)),
    pageTemplateSelectStore: types.maybeNull(types.late(() => types.reference(PageTemplateSelectState))),
    codeFilterSelectState: types.maybeNull(types.reference(CodeFilterSelectState)),
    codeOwnerFilterSelectState: types.maybeNull(types.reference(CodeOwnerFilterSelectState)),
    dimensionSelectViewState: types.maybeNull(types.reference(DimensionSelectViewState)),
    tagFilterSelectState: types.maybeNull(types.reference(TagFilterSelectState)),
    groupFilterSelectState: types.maybeNull(types.reference(GroupFilterSelectState)),
    selectedGroups: types.optional(types.array(types.safeReference(Group)), []),
    selectedTemplate:types.optional(types.array(types.safeReference(QrTemplate)),[]),
    modalStore: types.maybeNull(ModalState),
    modal2Store: types.maybeNull(Modal2State),
    toastStore: types.maybeNull(GenericToastState),
    selectedTags: types.optional(types.array(types.reference(Tag, {acceptsUndefined: true})), []),
    deleteStateRef: types.maybeNull(DeleteState),
    auditStateRef: types.maybeNull(AuditViewState),
    editModalMode: types.optional(types.boolean, false),
    isFilterTypeOR: types.optional(types.boolean, true),
    isFilterVisible: types.optional(types.boolean, false),
    experienceData: types.optional(types.string, '{}'),
    filterGroup: types.optional(types.boolean, true),
    isElasticSearchEnabled: types.optional(types.boolean, false),
    searchTxt : types.optional(types.string,''),
    isInitialLoad: types.optional(types.boolean, true),
    isAuditActionEnabled: types.optional(types.boolean, false),
    optionalColumns: types.optional(types.array(types.string), []),
    columnNameMapping: types.optional(types.map(types.string), {}),
    hasLoadedFiltersFromSession: types.optional(types.boolean, false), // Commented out - using afterCreate approach
    tableOptionalColumns: types.optional(types.array(types.string), [])
  }).volatile((self) => ({
    modalComponent: types.object,
    modalComponentStore: types.object,
    modalSize: types.str,
    modalClassName: types.str,

    modal2Component: types.object,
    modal2ComponentStore: types.object,
    modal2Size: types.str,
    modal2ClassName: types.str,

    toastComponentStore: types.object,
    row: types.object,
    base64SrcImage: types.str,
  }))
  .actions((self) => ({
    setBase64SrcImage(str){
      self.base64SrcImage = str;
    },
    createBase64Image: flow(function* createBase64Image(experienceData) {
      self.mediaFileLoading = true;
      self.mediaFileError = null;
      try {
        const mediaFileManager = getEnv(self).mediaFileManager;
        const mediaFiles = self.row?.mediaFiles;
        let file = null;
        if (mediaFiles && Object.values(mediaFiles).length > 0) {
          file = yield mediaFileManager.getMediaFile(Object.values(mediaFiles)[0]);
        } else {
          throw new Error("No media files found in self.row.mediaFiles");
        }

        if (isEpsFormat({"type": file?.fileLocation?.fileFormat })) {

          const epsUrl = experienceData?.fileUrl || "";
          try {
            const base64SrcImg = yield mediaFileManager.convertEpsToBase64(epsUrl);
            self.setBase64SrcImage(base64SrcImg)
          } catch (error) {
            self.mediaFileError = "Failed to convert EPS file: " + error.message;
          }
        }else{
          self.setBase64SrcImage("")
          return null
        }
        self.mediaFile = file;
      } catch (error) {
        self.mediaFileError = error.message;
      } finally {
        self.mediaFileLoading = false;
      }
    }),
    setDeleteStateRef(delRef){
      self.deleteStateRef = delRef;
    },
    setAuditStateRef(auditRef) {
      self.auditStateRef = auditRef;
    },
    toggleFilterVisible() {
      self.isFilterVisible = !self.isFilterVisible
      self.saveFiltersToSessionStorage()
    },
    setEditModalMode(bool){
      self.editModalMode = bool;
      self.modalClassName = self.editModalMode ? "design-and-view-experience" : "preview-experience";
    },
    setColumnSelectorOpen() {
      self.isColumnSelectorOpen = !self.isColumnSelectorOpen
    },
    toggleColumnVisibility(id) {
      if (self.optionalColumns.includes(id)) {
        self.optionalColumns.remove(id);
      } else {
        self.optionalColumns.push(id);
      }
      self.saveColumnsToSession();
    },
    setOnSelectChanges() {
      if (self.isInitialLoad) {
        self.isInitialLoad = false; // Set to false after first load
        return;
      }
      self.groupSelectStore.setMultiSelectConsumer(self.selectGroup)

      self.tagSelectStore?.setConsumer(self.selectTag)

      self.codeFilterSelectState?.setConsumer(self.selectFilter)

      self.codeOwnerFilterSelectState?.setConsumer(self.selectFilter)
      self.dimensionSelectViewState?.setFilterConsumer(self.selectFilter)
      self.tagFilterSelectState?.setConsumer(self.selectFilter)
      self.groupFilterSelectState?.setConsumer(self.selectFilter)
    },
    onSearchChange(txt){
      console.log(txt?.currentTarget?.value);
      const searchText = txt?.currentTarget?.value;
      self.searchTxt = searchText
      if(searchText){
        if(searchText.length > 2) {
          self.store.removeSearchParams(["q"]);
          self.store.addSearchParams({ q: searchText })
          self.isElasticSearchEnabled ? self.search() : self.refresh()
        } else {
          self.store.removeSearchParams(["q"])
        }
      } else {
        self.store.removeSearchParams(["q"]);
        self.isElasticSearchEnabled ? self.search() : self.refresh()
      }
      self.saveFiltersToSessionStorage()
    },
    selectFilter(obj, covered){
      self.saveFiltersToSessionStorage()

      // Remove dimensions mentioned in covered array
      const dimensionKeysToRemove = covered.filter(key => key.startsWith('dimension['));
      if (dimensionKeysToRemove.length > 0) {
        self.store.removeSearchParams(dimensionKeysToRemove);
      }

      // Remove other filter keys mentioned in covered array that are not in obj
      const otherKeysToRemove = covered.filter(key => !key.startsWith('dimension['));
      if (otherKeysToRemove.length > 0 && obj) {
        // Only remove keys that are NOT present in the new obj
        const keysNotInObj = otherKeysToRemove.filter(key => !obj.hasOwnProperty(key) || lodash.isEmpty(obj[key]));
        if (keysNotInObj.length > 0) {
          self.store.removeSearchParams(keysNotInObj);
        }
      }

      if(obj){
        const transpiledDimensions = {};
        if(!lodash.isEmpty(obj.dimensions)){
          obj.dimensions.forEach(dimension => {
            const [uuid, value] = dimension.split('_');
            if (transpiledDimensions.hasOwnProperty(`dimension[${uuid}]`)) {
              transpiledDimensions[`dimension[${uuid}]`] += ',' + value;
            } else {
              transpiledDimensions[`dimension[${uuid}]`] = value;
            }
          });
          delete obj.dimensions
        }

        const params = lodash.mapValues(obj,(value, key) => {
          return {}[key] = value.join(',');
        });
        self.store.addSearchParams({...params,...transpiledDimensions})
        self.isElasticSearchEnabled ? self.search() : self.refresh()

        const searchParams = self.store.getSearchParams();
        const currentDimensions = Object.keys(searchParams).filter(key => key.startsWith('dimension['));
        const currentGroupUuid = searchParams.groupUuid ? [searchParams.groupUuid] : [];
        //const currentGroupUuid = obj.groupUuid || [];

        const prevGroupUuid = self.prevGroupUuid || [];
        const prevDimensions = self.prevDimensions || [];

        // Check if groups changed
        if (!lodash.isEqual(prevGroupUuid, currentGroupUuid)) {
          self.filterGroup = false;
          self.prevGroupUuid = currentGroupUuid;
          self.fetchFilters();
        }
        // Check if dimensions changed (added, removed, or modified)
        else if (!lodash.isEqual(prevDimensions, currentDimensions)) {
          self.filterGroup = false;
          self.prevDimensions = currentDimensions;
          self.fetchFilters();
        }
        // Initial case: if groups or dimensions have values
        else if (currentGroupUuid?.length>0 || currentDimensions?.length > 0) {
          self.filterGroup = false;
          self.fetchFilters();
        }
      }
    },
    selectGroup(obj) {
      obj = obj || []
      self.store.addSearchParams({groupUuid: obj.flatMap((row) => row.uuid).join(",")})
      self.isElasticSearchEnabled ? self.search() : self.refresh()
    },
    selectTag(obj) {
      obj = obj || []
      self.store.addSearchParams({tags: obj.flatMap((row) => row.uuid).join(",")})
      self.isElasticSearchEnabled ? self.search() : self.refresh()
    },
    saveColumnsToSession() {
      try {
        const columns = getSnapshot(self.optionalColumns);
        sessionStorage.setItem("codeListColumns", JSON.stringify(columns));
      } catch (e) {
        console.warn("Failed to save column visibility to sessionStorage", e);
      }
    },
    loadColumnsFromSession() {
      try {
        const saved = sessionStorage.getItem("codeListColumns");
        if (saved) {
          self.optionalColumns = JSON.parse(saved);
        }
      } catch (e) {
        console.warn("Failed to load column settings from sessionStorage", e);
      }
    },
    selectFilterMethod() {
        if (!self.isFilterTypeOR) {
          self.isFilterTypeOR = !self.isFilterTypeOR
          self.store.addSearchParams({tagFilterType: formConstants.Mode.OR})
        } else {
          self.isFilterTypeOR = !self.isFilterTypeOR
          self.store.addSearchParams({tagFilterType: formConstants.Mode.AND})
        }
      self.isElasticSearchEnabled ? self.search() : self.refresh()
      self.saveFiltersToSessionStorage()
    },
    setColumns(col) {
      self.columns = col
    },
    setColumnNameMapping(mapping) {
      self.columnNameMapping = mapping
    },
    setTableOptionalColumns(columns) {
      self.tableOptionalColumns = columns;
    },
    handleExportClick(routerStore) {
      const exportType = "default";

      const dataExportTypeStore = resolveIdentifier(DataExportTypeStore, getRoot(self), 'dets');
      const defaultExportSchema = dataExportTypeStore.dataExportTypes.find(e => e.type === exportType);

      if (lodash.isEmpty(defaultExportSchema)) {
        console.error("'default' data-export type not in root");
        routerStore.goTo('dashboard');
      }

      const defaultExportUISchema = JSON.parse(defaultExportSchema.uiSchema);
      const allQRColumns = defaultExportUISchema?.columns?.["ui:options"]?.columnOptions?.find(op => op.label === "QR Codes")?.options;

      const columnsToSelect = ["Code Name", "Code Type", "Code URL", "Created At", "Group Name", "Landing URL", "Status", "Updated At"];
      const selectedFilter = self.store.getSearchParams();

      const filterOnColumn = [];
      let selectedColumns = allQRColumns.filter(col => columnsToSelect.includes(col.label));
      let tags = [];
      let useOr = false;
      if (!lodash.isEmpty(selectedFilter)) {
        const createColumnFilter = (name, condition, value) => {
          return {
            name,
            condition,
            value
          };
        };

        const filteredDimensions = Object.keys(selectedFilter).filter(k => k.startsWith('dimension'));
        const parentDimensionUuids = filteredDimensions.map(d => d.match(/\[(.*?)\]/)[1]);

        const filterKeys = [
          {columnName: 'status', key: 'status', condition: '='},
          {columnName: 'shorturldomain', key: 'domain', condition: '='},
          {columnName: 'createdby', key: 'createdBy', condition: '='},
          {columnName: 'codetype', key: 'type', condition: '='},
          {columnName: 'codename', key: 'q', condition: 'like'},
          {columnName: 'groupuuid', key: 'groupUuid', condition: '='},
          // TODO: uncomment this to add filters once we have respective tables / columns
          // { columnName: 'tags_cmpv2.uuid', key: 'tags', condition: '=' },
          // { columnName: 'qrcodes_cmpv2.behavior', key: 'behavior', condition: '=' },
          // ...(parentDimensionUuids?.map(uuid => ({ columnName: 'dimensions_cmpv2.uuid', key: `dimension[${uuid}]`, condition: '=' })) || []),
        ];

        // selectedFilter: {k: string || comma seperated string}
        // filterKeys[].key is mapped with selectedFilter.k
        // if there's a match then we add the filter on filterKeys[].columnName

        filterKeys.forEach(({columnName, key, condition}) => {
          if (Object.keys(selectedFilter).includes(key)) {
            // if user selects the filter we check the value and add to data-export filters
            const value = selectedFilter[key];
            if (value.includes(',')) {
              useOr = true;
              value.split(',').forEach((v) => {
                filterOnColumn.push(createColumnFilter(columnName, condition, v));
              });
            } else {
              filterOnColumn.push(createColumnFilter(columnName, condition, selectedFilter[key]));
            }
          }
        });
        tags = selectedFilter["tags"]?.split(",");
      }

      const item = {
        action: 'create',
        type: exportType,
        name: "QR_Codes_Base_Export",
        hideActions: getBase64String(["edit"]),
        ...(tags?.length && {tags: getBase64String(tags)}), // this will add tags to the data export; it doesn't add tag filters on the export
        definition: getBase64String({
          columns: selectedColumns.map(col => col.value),
          filter: {
            toggle: Boolean(filterOnColumn.length),
            definition: {
              onColumns: filterOnColumn
            }
          },
          useOr: useOr,
          orderBy: {
            toggle: false
          }
        })
      };
      routerStore.goTo('/data/exports/create',{
        queryParams: item
      });
    },
  fetchAppliedImage:flow(function* f() {

  }),
    handleRowClick(component, title, componentStore, size = "",className = "",row) {
      self.modalComponentStore = componentStore;
      self.modalComponent = component;
      self.modalSize=size;
      self.modalClassName=className;
      self.row = row
      self.modalStore.toggle()
      self.modalStore.handleTitle(title)
    },
    handleModal2PopUp(component, title, componentStore, size = "", className = "") {
      self.modal2ComponentStore = self[componentStore];
      self.modal2Component = component;
      self.modal2Size = size;
      self.modal2ClassName = className;
      self.modal2Store.toggle()
      self.modal2Store.handleTitle(title)
    },
    updateExperienceData(data) {
      self.experienceData = JSON.stringify(data)
    },
    handleClick(componentStore,component){
      self.modalComponent = component;
      self.modalComponentStore = componentStore;
      self.modalStore.handleTitle(componentStore.title)
      self.modalStore.toggle()
    },
    refresh() {
      self.store.fetchAll()
    },
    search () {
      self.store.searchResults()
    },
    addRow(data) {
      //? P / ...
    },
    deleteRow(uuid) {
      console.log("deleteRow", uuid);
      // self.store.items = self.store.items.filter(item => item.uuid !== uuid)
      // commented out above is a filter that is not remounting component properly, having something to do with mobX,
      // therefore we fetchAll for now
      self.store.fetchAll()
    },
    afterCreate() {
      self.modalComponent = (<></>)
      self.modalComponentStore = {}
      // console.log("Instantiated " + getType(self).name)
      // self.groupSelectStore.setConsumer((g)=> {
      //   self.store.addSearchParams({ groupUuid: g.uuid })
      //   self.refresh()
      // })
      //
      // self.tagSelectStore.setConsumer((selectedTags)=> {
      //   const tagUuids = (selectedTags || []).map((val)=>{
      //     return val.value.uuid
      //   })
      //   self.store.addSearchParams({ tags: tagUuids })
      //   self.refresh()
      // })
    },
    publishRow(obj) {
      self.store.publish(obj);
    },
    completeRow(obj) {
      self.store.complete(obj);
    },
    auditRow(obj) {
      self.store.audit(obj)
    },
    fetchAudit(obj) {
      self.store.fetchAudit(obj)
    },
    fetchRow(){
      self.store.fetchAll();
    },
    fetchFilters() {
      self.store.fetchAllFilters();
    },
    saveFiltersToSessionStorage() {
      persistFiltersToSession(self, filterSessionConstants.CODE_SESSION_STORAGE_KEY, "codeFilterSelectState");
    },

    loadFiltersFromSessionStorage() {
      if (self.hasLoadedFiltersFromSession) {
        return;
      }
      loadFiltersFromSession(self, filterSessionConstants.CODE_SESSION_STORAGE_KEY, "codeFilterSelectState");
      applySearchParamsFromFilters(self, "codeFilterSelectState");
      self.isElasticSearchEnabled ? self.search() : self.refresh();
      self.hasLoadedFiltersFromSession = true;
    },

    clearFilters() {
      clearAllFilters(self, filterSessionConstants.CODE_SESSION_STORAGE_KEY, "codeFilterSelectState");
      self.hasLoadedFiltersFromSession = false;
    },
    addSearchParamsFromLoadedFilters() {
      applySearchParamsFromFilters(self, "codeFilterSelectState");
    }

  })).views( (self) => ({
    shouldRenderSecondTabInPreview() {
      return self.row.behavior === codeBehavior.DYNAMIC
    },
    shouldRenderCopyUrl(){
      return self.row?.behavior !== codeBehavior.STATIC
    }
}));


export default CodeListState
